import {baseConfig} from './helpers/baseConfig'
import {klarnaConfig} from './klarna/config'
import {cardConfig, bcmcConfig} from './card/config'
import {paypalConfig} from './paypal/config'
import {applePayConfig} from '@adyenPWA/lib/components/applepay/config'
import {amazonPayConfig} from './amazonpay/config'

/**
 * Configures payment methods by merging default configurations with specific configurations
 * for each payment method type. Supports additional custom configurations for each method.
 *
 * @param {Object} params - The parameters for configuring payment methods.
 * @param {Array<Object>} params.paymentMethods - An array of payment method objects, each containing a `type` property.
 * @param {Object} [params.additionalPaymentMethodsConfiguration] - An optional object containing additional configurations for specific payment method types.
 * @param {Object} props - Additional properties to be passed to configuration functions.
 * @returns {Object} An object where keys are payment method types and values are their respective configurations.
 */
export const paymentMethodsConfiguration = ({
    paymentMethods = [],
    additionalPaymentMethodsConfiguration,
    ...props
}) => {
    const defaultConfig = baseConfig(props)
    if (!paymentMethods?.length) {
        return defaultConfig
    }

    const paymentMethodsConfig = {
        card: cardConfig(props),
        bcmc: bcmcConfig(props),
        klarna: klarnaConfig(props),
        klarna_account: klarnaConfig(props),
        klarna_paynow: klarnaConfig(props),
        paypal: paypalConfig(props),
        applepay: applePayConfig(props),
        amazonpay: amazonPayConfig(props)
    }

    return Object.fromEntries(
        paymentMethods.map((paymentMethod) => {
            const type = paymentMethod.type === 'scheme' ? 'card' : paymentMethod.type
            const basePaymentMethodConfig = Object.hasOwn(paymentMethodsConfig, type)
                ? paymentMethodsConfig[type]
                : defaultConfig
            return additionalPaymentMethodsConfiguration?.[type]
                ? [
                      type,
                      {...basePaymentMethodConfig, ...additionalPaymentMethodsConfiguration[type]}
                  ]
                : [type, basePaymentMethodConfig]
        })
    )
}
